import React, { useState, useEffect, useMemo, useRef } from "react";
import { Table, Tooltip, message, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  GetInvoicesService,
  GetInvoicesService_NEW,
  UpdateInvoiceFlagService,
  UpdateInvoiceStageAndArchiveStatusService,
} from "../../../Services/InvoiceService/InvoiceService";
import "./Archieve.css";
import CommonTable from "../../../Components/CustomTable/CustomTable";
import ColumnSettings from "../../../Components/ColumnFilter/ColumnFilter";
import FilterBtn from "../../../Components/CustomButtons/FilterBtn/FilterBtn";
import AlertMessage from "../../../Components/AlertMessage/AlertMessage";
import { InvoiceFieldMap } from "../../../Services/InvoiceService/constants";
import { IoSettingsSharp } from "react-icons/io5";
import { InvoiceFilter } from "../../../Components/InvoiceFilter";
import { MdOutlineFilterAlt } from "react-icons/md";
import { FiChevronDown, FiSearch } from "react-icons/fi";
import { TbFilter } from "react-icons/tb";
import { FiTrash2 } from "react-icons/fi";
import ConfirmPopup from "../../../Components/ConfirmPopup/ConfirmPopup";
import SupplierStatementFilter from "../../../Components/SupplierStatementFilter/SupplierStatementFilter";
import AdvancedSearch from "../../../Components/InvoiceSearch/AdvancedSearch";
import { toInvoiceSearchPayload } from "../../../Components/InvoiceSearch/AdvanceType";
// Note: Ensure your import path for ReviewInvoiceView points to the correct location
import ArchiveInvoiceView from "../7Archive/ArchiveInvoiceView/ArchiveInvoiceView";
import { setSelectedInvoice } from "../../../Reducer/SelectedInvoiceReducer";
import { InvoiceFilterFieldsPayload } from "../../../Components/InvoiceFilter/InvoiceFilter.fields";
import { ActiveFilterBar } from "../../../Components/InvoiceFilter/ActiveFilterBar";
import SelectedClientCard from "../../../Components/SelectedClientCard/SelectedClientCard";
import Loader from "../../../Components/Loader/Loader";
import SupplierStatementArchive from "./SupplierStatementArchive";
import "../3Inbox/Tools/Toolsmenu.css";

interface RowData {
  id: number;
  stageId: number;
  isFlagged: boolean;
  user: string;
  description: string;
  date: string;
  supplier: string;
  category: string;
  advanced: string;
  documentOwner: string;
  total: string;
}

const Archive: React.FC = () => {
  const INVOICE_PER_FETCH = 100;
  const selectedClient = useSelector((state: any) => state.selectedClient);
  const applicationType = useSelector((state: any) => state.applicationType.applicationType);
  const isSupplierStatements = applicationType === "supplierStatements";

  // Data States
  const [loading, setLoading] = useState(false);
  const [totalInvoiceCount, setTotalInvoiceCount] = useState(0);
  const [invoices, setInvoices] = useState<RowData[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  // State to control InvoiceFilter popup
  const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);
  // State to store active filter values
  const [activeFilters, setActiveFilters] = useState<any>(null);
  // Selection States
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isOpenAdvance, setIsOpenAdvance] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ssFilterStatus, setSsFilterStatus] = useState<string | null>(null);
  // ADD THIS REF ONLY (keep all other code same)

  const isLoadingMoreRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debounceRef = useRef<any>(null);
  interface SortState {
    key: keyof typeof InvoiceFieldMap | "default" | null;
    direction: boolean;
  }
  // View control states requested by the user
  const [invoiceView, setInvoiceView] = useState(false);
  const [sortSettings, setSortSettings] = useState<SortState>({
    key: "default",
    direction: false,
  });

  const refreshInvoices = () => resetAndFetch();

  const [fullinvoices, setFullInvoices] = useState<any[]>([]);

  const dispatch = useDispatch();
  const cursorRef = useRef<string>("");
  const savedSort = localStorage.getItem("sortSettings");
  const sortReverseRef = useRef<boolean>(false);
  const sortKeyRef = useRef<keyof typeof InvoiceFieldMap | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const activeFiltersRef = useRef<any>(null);
const activeAdvancedSearchRef = useRef<any>(null);

  const [activeAdvancedSearch, setActiveAdvancedSearch] = useState<any>(null);
  const advancedSearchRef = useRef<{ resetForm: () => void }>(null);
  // Fetch invoices
  const fetchData = async (
    loadMore = false,
    reverse: boolean = false,
    key: keyof typeof InvoiceFieldMap | null = null,
    filters: any = null,
    searchFields: any = null,
    limit: number = INVOICE_PER_FETCH,
  ) => {
    if (applicationType === "supplierStatements") return;
    if (!selectedClient.selectedClient?.userId) return;
    const apiSearchFields = toInvoiceSearchPayload(searchFields);
    try {
      if (!loadMore) setLoading(true);
      const result = await GetInvoicesService_NEW({
        userId: selectedClient.selectedClient.userId,
        after: cursorRef.current || null,
        order: key ?? (savedSort ? JSON.parse(savedSort).key : "default"),
        reversed: key !== null ? reverse : (savedSort ? JSON.parse(savedSort).direction : false),
        stageIds: [],
        archiveStatuses: ["Archived", "AlreadyExported"],
        limit: limit || INVOICE_PER_FETCH,
        filterFields: filters,
        searchFields: apiSearchFields,
      });
     if (result?.Items !== undefined) {
        setTotalInvoiceCount(result.TotalItem ?? 0);

        setInvoices((prev) => {
          if (!loadMore) return result.Items;

          const existingIds = new Set(prev.map((item) => item.id));

          const newItems = result.Items.filter(
            (item: any) => !existingIds.has(item.id)
          );

          return [...prev, ...newItems];
        });

        cursorRef.current = result.NextCursor || "";
        setHasMore(result.HasMore);
      }


      const fullresult = await GetInvoicesService_NEW({
        userId: selectedClient.selectedClient.userId,
        after: null, // <-- always null for full fetch
        order: key ?? (savedSort ? JSON.parse(savedSort).key : "default"),
        reversed:
          key !== null
            ? reverse
            : savedSort
              ? JSON.parse(savedSort).direction
              : false,
         stageIds: [],
        archiveStatuses: ["Archived", "AlreadyExported"],
        limit: result.TotalItem || totalInvoiceCount, // <-- FIX: use fresh result, not stale state
        filterFields: filters,
        searchFields: apiSearchFields,
      });
      setFullInvoices(fullresult?.Items || []);
      setFullInvoices(fullresult.Items);
    } catch (error) {
      console.error("Error fetching invoice data:", error);
      message.error("Failed to load invoices.");
    } finally {
      setLoading(false);
    }
  };
  // REPLACE resetAndFetch ONLY
  console.log("saved sort in archive:", cursorRef.current);
  const resetAndFetch = async () => {
    setInvoices([]);
    cursorRef.current = "";
    setSelectedRowKeys([]);
    setSelectAll(false);
    setHasMore(true);

   const sortDir = savedSort ? JSON.parse(savedSort).direction : false;
  const sortKey = savedSort ? JSON.parse(savedSort).key : null;

  await fetchData(
    false,
    sortDir,
    sortKey,
    activeFilters,        
    activeAdvancedSearch,
    invoices.length || 100
  );
  };
  const sortInvoices: any = async (
    reverse: boolean,
    key: keyof typeof InvoiceFieldMap | null,
  ) => {
    cursorRef.current = "";
    sortKeyRef.current = key;
    sortReverseRef.current = reverse;
    setIsLoadingMore(false); // start loading
    const newSettings = { key, direction: reverse };
    setSortSettings(newSettings);
    localStorage.setItem("sortSettings", JSON.stringify(newSettings));
    await fetchData(false, reverse, key,activeFilters, activeAdvancedSearch, invoices.length); // fetch next batch
    setIsLoadingMore(false);
    setSelectedRowKeys([]); // clear selections on sort
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);

    // reset cursor for new search
    setCursor(""); // Assuming setCursor is the correct setter for the cursor state

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchData(
        false,
        false,
        null,
        activeFilters,
        value ? { searchTerm: value } : null
      );
    }, 500); // 500ms delay
  };
  const [flagOverrides, setFlagOverrides] = useState<
    Record<string | number, boolean>
  >({})
  useEffect(() => {
    setInvoices([]);
    setSelectedRowKeys([]);
    fetchData();
  }, [selectedClient.selectedClient?.userId]);

  // Sync select all
  useEffect(() => {
    setSelectAll(
      invoices.length > 0 && selectedRowKeys.length === invoices.length,
    );
  }, [selectedRowKeys, invoices]);

  //for the activefilters
  useEffect(() => {
    if (selectedClient.selectedClient?.userId) {
      cursorRef.current = "";
      setInvoices([]);
      fetchData(false, false, null, activeFilters);
    }
  }, [activeFilters]);

  useEffect(() => {
  activeFiltersRef.current = activeFilters;
}, [activeFilters]);

useEffect(() => {
  activeAdvancedSearchRef.current = activeAdvancedSearch;
}, [activeAdvancedSearch]);

  // Handle checkbox changes
  const handleCheckboxChange = (row: RowData, checked: boolean) => {
    setSelectedRowKeys((prev) => {
      if (checked) return prev.includes(row.id) ? prev : [...prev, row.id];
      else return prev.filter((id) => id !== row.id);
    });
  };

  const handleSelectAll = () => {
    if (selectAll) setSelectedRowKeys([]);
    else setSelectedRowKeys(invoices.map((row) => row.id));
    setSelectAll(!selectAll);
  };

  const handleClearSelection = () => {
    setSelectedRowKeys([]);
    setSelectAll(false);
  };

  // Infinite scroll

  const loadMoreInvoicesOnScroll = async () => {
    if (hasMore && !isLoadingMore) {
      isLoadingMoreRef.current = true;

      setIsLoadingMore(true);

      await fetchData(
        true,
        sortReverseRef.current,
        sortKeyRef.current,
        activeFiltersRef.current,      
        activeAdvancedSearchRef.current  
      );

      setIsLoadingMore(false);

      // isLoadingMoreRef.current = false;
    }
  };
  const handleUnArchive = async () => {
    try {
      await UpdateInvoiceStageAndArchiveStatusService({
        invoiceIds: selectedRowKeys,
        stageId: null, //invoices[0]?.stageId,
        archiveStatus: "UnArchived",
      });
      message.success("Invoices Unarchived Successfully");
      //setSelectedRowKeys([]);
    } catch (error) {
      console.error("Error unarchiving invoices:", error);
      message.error("Failed to unarchive invoices.");
    } finally {
      setLoading(false);
      await resetAndFetch();
    }
  };

  const handleDeleteSelected = async () => {
    
    AlertMessage({ type: "success", text: "Deleted successfully" });
  };

  const handleSsFilterApply = ({ status }: { status: string | null }) => {
    setSsFilterStatus(status);
  };
  const handleScroll = () => {
    const div = listRef.current;
    if (!div || isLoadingMore || !hasMore) return;

    const scrollTop = div.scrollTop;
    const scrollHeight = div.scrollHeight;
    const clientHeight = div.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      // scroll bottom close to 100px
      loadMoreInvoicesOnScroll();
    }
  };
  useEffect(() => {
    const div = listRef.current;
    if (!div) return;

    div.addEventListener("scroll", handleScroll);
    return () => div.removeEventListener("scroll", handleScroll);
  }, [listRef, isLoadingMore, hasMore, cursor]); // include cursor dependency

  // Columns
  const columns = useMemo(
    () => [
      {
        key: "selected",
        title: (
          <input
            type="checkbox"
            placeholder="checkbox"
            checked={selectAll}
            onClick={(e) => e.stopPropagation()}
            onChange={handleSelectAll}
          />
        ),
        dataIndex: "",
        isCheckbox: true,
        showSortArrows: false,
      },
      {
        key: "isFlagged",
        title: "isFlagged",
        dataIndex: "isFlagged",
        showSortArrows: false,
        isFlag: true,
      },
      { title: "ID", dataIndex: "id", key: "invoiceId" },
      {
        title: "Status",
        dataIndex: "stageId",
        key: "Status",
        isStatus: true,
        showSortArrows: true,
      },
      {
        title: "Submitted By",
        dataIndex: "submittedBy",
        key: "submittedBy",
        showSortArrows: true,
        render: (text: string | null, record: any) =>
          record.submissionMethod?.toLowerCase() === "email"
            ? ""
            : text || "",
      },
      { title: "Date", dataIndex: "invoiceDate", key: "date", isDate: true },
      {
        title: "User",
        dataIndex: "documentOwner",
        key: "documentOwner",
        showSortArrows: false,
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "Description",
        showSortArrows: true,
        render: (text: string) => (
          <Tooltip title={text}>
            <span>
              {text?.length > 15 ? `${text.substring(0, 15)}...` : text}
            </span>
          </Tooltip>
        ),
      },

      { title: "Supplier", dataIndex: "supplier", key: "supplier" },
      {
        title: "Category",
        dataIndex: "category",
        key: "category",
        showSortArrows: true,
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        // isStatus: true,
        showSortArrows: false,
      },
      {
        title: "Total",
        dataIndex: "totalAmount",
        key: "total",
        render: (val: any) => (
          <span>{val != null ? parseFloat(val).toFixed(2) : "0.00"}</span>
        ),
      },
      {
        title: "Tax",
        dataIndex: "taxAmount",
        key: "TAXAMOUNT",
        showSortArrows: true,
        // showSortArrows: true,
        // render: (taxAmount: number) => ()
      },
      {
        title: "Document Ref",
        dataIndex: "documentReference",
        key: "DOCUMENTREFERENCE ",
        showSortArrows: true,
      },
      {
        title: "Due Date",
        dataIndex: "dueDate",
        key: "dueDate",
        // isStatus: true,
        showSortArrows: true,
        isDate: true,
        render: (text: string) => {
          const date = new Date(text); // Parse the ISO date
          const formattedDate = date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }); // Formats as dd/mm/yyyy
          return (
            <Tooltip title={formattedDate}>
              <span>{formattedDate}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Paid",
        dataIndex: "paid",
        key: "Paid",
        // isStatus: true,
        showSortArrows: true,
      },
      {
        title: "Payment",
        dataIndex: "payment",
        key: "Payment",
        // isStatus: true,
        showSortArrows: true,
      },
      {
        title: "Submission Method",
        dataIndex: "Submission Method",
        key: "Submission Method",
        // isStatus: true,
        showSortArrows: true,
      },
      {
        title: "Upload Date",
        dataIndex: "createdDate",
        key: "createdDate",
        isDate: true,
        // isStatus: true,
        showSortArrows: true,
      },
    ],
    [selectAll],
  );

  const alwaysVisibleColumns = ["selected", "isFlagged", "id", "stageId"];
  const selectedColumns: string[] = JSON.parse(
    localStorage.getItem("selectedColumns") || "[]",
  );
  const filteredColumns = columns.filter(
    (col) =>
      alwaysVisibleColumns.includes(col.key as string) ||
      selectedColumns.includes(col.title as string),
  );

  // Logic to handle filter application
  const handleApplyFilters = (filterValues: any) => {
    cursorRef.current = "";
    sortKeyRef.current = null; // ← reset sort
    sortReverseRef.current = false;
    setInvoices([]);
    setActiveFilters(filterValues);
    setIsFilterPopupOpen(false);
    fetchData(false, false, null, filterValues);
  };
  if (!selectedClient.selectedClient?.userId)
    return <h1>Please select a client.</h1>;
  ;
  const handleResetFilters = () => {
  setActiveFilters(null);
  setActiveAdvancedSearch(null);
  cursorRef.current = "";
  advancedSearchRef.current?.resetForm();
  setInvoices([]);
  fetchData(false, false, null, null, null);
  
  
};

  const handleAdvancedApply = (searchFields: any) => {
    setActiveAdvancedSearch(searchFields);
    cursorRef.current = "";
    setInvoices([]);
    fetchData(false, false, null, activeFilters, toInvoiceSearchPayload(searchFields));
    setIsOpenAdvance(false);
  };

 const handleAdvancedReset = () => {
  setActiveAdvancedSearch(null);  // ← this must happen first, alone
  cursorRef.current = "";
  advancedSearchRef.current?.resetForm();
  fetchData(false, false, null, activeFilters, null);  // ← no setInvoices([]) before this
};

  const handleRemoveFilter = (key: keyof InvoiceFilterFieldsPayload) => {
  cursorRef.current = "";
  setInvoices([]);
  const updated = { ...activeFilters, [key]: null };
  setActiveFilters(updated);        // triggers useEffect
};
const handleAdvancedRemove = (updated: any) => {
  setActiveAdvancedSearch(updated);
  cursorRef.current = "";
  setInvoices([]);
  fetchData(false, false, null, activeFilters, toInvoiceSearchPayload(updated));   // Archive/Ready
};

  const handleFlagClick = async (row: RowData) => {
    const currentFlagState = flagOverrides[row.id] ?? row.isFlagged;
    const newFlagState = !currentFlagState;

    // Optimistic local update
    setFlagOverrides((prev) => ({ ...prev, [row.id]: newFlagState }));

    const result = await UpdateInvoiceFlagService(Number(row.id), newFlagState);

    if (!result.success) {
      // Revert on failure
      setFlagOverrides((prev) => ({ ...prev, [row.id]: currentFlagState }));
    }
  };


  const handleInvoiceClick = (row: RowData) => {
    sessionStorage.setItem("selectedInvoiceId", row.id.toString());

    //localStorage.setItem("part1_currentPage", currentPage.toString());
    dispatch(setSelectedInvoice(row));
    setInvoiceView(true);
    // makeInvoiceViewed(row.id, row.stageId);
  };
  console.log("invoices in ready stage:", invoices.length);

  const hasActiveSearch = searchTerm || activeFilters ||
  (activeAdvancedSearch && Object.values(activeAdvancedSearch).some(v => v !== "" && v !== null));

const isGenuinelyEmpty = invoices.length === 0 && !hasActiveSearch && !loading;
const showToolbar = !isGenuinelyEmpty;
if (applicationType === "supplierStatements") {
  return <SupplierStatementArchive />;
}

return (
    <>
      {invoiceView ? (
        <ArchiveInvoiceView
          setInvoiceView={setInvoiceView}
          refreshInvoices={refreshInvoices}
          sortSettings={sortSettings}
          fullinvoices={fullinvoices}
          totalInvoiceCount={totalInvoiceCount}
        />
      ) : (
        <>
          <div className="selected-client-toolbar">
            {!isSupplierStatements && <SelectedClientCard />}

            {isSupplierStatements ? (
              showToolbar && (
                <div className="ss-toolbar">
              
                  <div className="ss-toolbar__left">
                    <button
                      type="button"
                      className="ss-action-btn ss-action-btn--icon"
                      disabled={selectedRowKeys.length === 0 || loading}
                      onClick={() => setShowDeleteConfirm(true)}
                      title="Delete"
                    >
                      <FiTrash2 size={15} />
                    </button>
                    <button
                      type="button"
                      className="ss-action-btn"
                      disabled={selectedRowKeys.length === 0 || loading}
                      onClick={handleUnArchive}
                    >
                      Unarchive
                    </button>
                  </div>

                  
                  <div className="ss-toolbar__right">
                    <div className="ss-search-box">
                      <FiSearch size={16} style={{ color: "#474646", flexShrink: 0 }} />
                      <input
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setIsOpenAdvance(true)}
                        className="advanced-btn"
                      >
                        Advanced...
                        <FiChevronDown style={{ marginLeft: "4px" }} />
                      </button>
                    </div>
                    <AdvancedSearch
                      setIsOpenAdvance={isOpenAdvance}
                      onClose={() => setIsOpenAdvance(false)}
                      FetchInvoices={fetchData}
                      onApply={handleAdvancedApply}
                      formRef={advancedSearchRef}
                      activeSearchFields={activeAdvancedSearch}
                    />
                    <div style={{ position: "relative" }}>
                      <button
                        type="button"
                        className="ss-filter-btn"
                        disabled={loading}
                        onClick={() => setIsFilterPopupOpen((o) => !o)}
                        title="Filter"
                      >
                        <TbFilter size={18} />
                      </button>
                      <SupplierStatementFilter
                        isOpen={isFilterPopupOpen}
                        onClose={() => setIsFilterPopupOpen(false)}
                        onApply={handleSsFilterApply}
                        activeStatus={ssFilterStatus}
                        options={[
                          { label: "Archive", value: "archived" },
                          { label: "Reconciled", value: "reconciled" },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              )
            ) : (
              <>
                {showToolbar && (
                  <div className="selected-client-toolbar__actions">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "5px 8px",
                        width: "300px",
                        background: "#fff",
                      }}
                    >
                      <FiSearch size={18} style={{ color: "#474646" }} />
                      <input
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        style={{
                          flex: 1,
                          border: "none",
                          outline: "none",
                          fontSize: "var(--font-size-base)",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setIsOpenAdvance(true)}
                        className="advanced-btn"
                      >
                        Advanced
                        <FiChevronDown style={{ marginLeft: "4px" }} />
                      </button>
                    </div>
                    <AdvancedSearch
                      setIsOpenAdvance={isOpenAdvance}
                      onClose={() => setIsOpenAdvance(false)}
                      FetchInvoices={fetchData}
                      onApply={handleAdvancedApply}
                      formRef={advancedSearchRef}
                      activeSearchFields={activeAdvancedSearch}
                    />
                    <InvoiceFilter
                      activeFilters={activeFilters}
                      isOpen={isFilterPopupOpen}
                      onClose={() => setIsFilterPopupOpen(false)}
                      onApply={handleApplyFilters}
                    />
                    <FilterBtn
                      onClick={() => setIsFilterPopupOpen(true)}
                      disabled={loading}
                      icon={MdOutlineFilterAlt}
                    />
                    <FilterBtn
                      onClick={() => setIsOpen(true)}
                      disabled={loading}
                      Icon={IoSettingsSharp}
                    />
                  </div>
                )}
                {isOpen && (
                  <ColumnSettings isOpen={true} onClose={() => setIsOpen(false)} />
                )}
              </>
            )}
          </div>
          {!isSupplierStatements && (
            <div className="tm-row">
              <button
                className="tm-trigger"
                disabled={selectedRowKeys.length === 0}
                onClick={handleUnArchive}
              >
                UnArchive
              </button>
            </div>
          )}
          {showToolbar && (
            <ActiveFilterBar
              activeFilters={activeFilters}
              activeAdvancedSearch={activeAdvancedSearch}
              onRemove={handleRemoveFilter}
              onReset={handleResetFilters}
              onAdvancedReset={handleAdvancedReset}
              onAdvancedRemove={handleAdvancedRemove}
            />
          )}

          <ConfirmPopup
            isOpen={showDeleteConfirm}
            title="Delete Invoice"
            message="Do you want to delete this invoice?"
            onConfirm={() => {
              setShowDeleteConfirm(false);
              handleDeleteSelected();
            }}
            onCancel={() => setShowDeleteConfirm(false)}
          />

            {loading ? (
              <Loader />
            ) : invoices.length === 0 ? (
              <h1 style={{ textAlign: "center", marginTop: "40px", color: "#888" }}>
                No invoices found for this client
              </h1>
            ) : (
          <CommonTable
            columns={filteredColumns}
            // data={invoices}
            data={invoices.map((row: RowData) => ({
              ...row,
              isFlagged: flagOverrides[row.id] ?? row.isFlagged,
            }))}
            infinityScroll={true}
            onFlagClick={handleFlagClick}
            loadMoreInvoicesOnScroll={loadMoreInvoicesOnScroll}
            selectedRowKeys={selectedRowKeys}
            onRowCheckboxChange={handleCheckboxChange}
            selectAll={selectAll}
            handleSelectAll={handleSelectAll}
            handleClearSelection={handleClearSelection}
            stageId={4}
            userId={selectedClient.selectedClient?.userId}
            sortInvoices={sortInvoices}
            totalCount={totalInvoiceCount}
            // invoiceView="Archive"
            onRow={(record: RowData) => ({
              onClick: (e) => {


                //  Otherwise open invoice
                handleInvoiceClick(record);
              },
            })}
          />
          )}
          <h2
            style={{
              textAlign: "center",
              color: "var(--color-brand)",
              padding: "10px",
            }}
          >
            {isLoadingMore
              ? "Loading..."
              : `${invoices.length} / ${totalInvoiceCount}`}
          </h2>


        </>
      )}
    </>
  );
};

export default Archive;
